/**
 * The curriculum model.
 *
 * Every lesson leads with the *cause* — why a result happens — and only then
 * attaches the conventional name, because a name you can derive is a name you keep.
 * A lesson is not finished when you have read it; it is finished when you have
 * written it, heard it, and used it.
 *
 * This is an original learning path inspired by the cause-first teaching philosophy
 * Allen Van Wert describes publicly. It is not a reproduction of his book and
 * implies no affiliation or endorsement. `licensedSourceMapping` exists so that an
 * authorised outline can be attached later without rewriting the engine.
 */

import type { Clef } from '../theory/staff';

export type TaskKind = 'ink' | 'ear' | 'creative';

/** Write something on the staff and be graded on it. */
export interface GuidedInkTask {
  prompt: string;
  clef: Clef;
  /** Expected pitches in scientific notation, e.g. ["C4","E4","G4"]. Order is ignored. */
  expect: string[];
  /** Grade by spelling and register, or by spelling alone. */
  ignoreOctave: boolean;
  hint: string;
}

/** Hear something and identify it. Examples are generated so they never memorise. */
export interface EarTask {
  prompt: string;
  /** Which generator builds the example. */
  kind: 'interval' | 'quality' | 'scaleDegree' | 'cadence';
  /** The answers offered. The generator picks one of these as the truth. */
  choices: string[];
}

/** Open-ended writing. Not graded — the point is to use the idea. */
export interface CreativeTask {
  prompt: string;
  clef: Clef;
  /** What a good answer demonstrates, shown after they mark it done. */
  lookFor: string;
}

export interface MasteryRule {
  /** Correct ear answers in a row before the lesson counts as heard. */
  earStreak: number;
  /** Whether the ink task must be passed. */
  requireInk: boolean;
  /** Whether the creative task must be marked done. */
  requireCreative: boolean;
  description: string;
}

export interface Lesson {
  id: string;
  /** 1-based position in the path. */
  sequence: number;
  title: string;
  /** The one-sentence idea. */
  idea: string;
  /** Why the result happens, before any terminology. */
  cause: string;
  /** A worked example, shown as engraved notation the learner can hear. */
  demonstration: {
    caption: string;
    clef: Clef;
    /** Each group is a chord or a single note, in scientific notation. */
    groups: string[][];
    labels?: (string | null)[];
  };
  guidedInkTask: GuidedInkTask;
  earTask: EarTask;
  creativeTask: CreativeTask;
  /** Shown with the unit when part of its brief-described scope is deferred. */
  scopeNote?: string;
  masteryRule: MasteryRule;
  /** Lesson ids that must be mastered first. */
  prerequisites: string[];
  /** Reserved for an authorised chapter mapping. Never invented. */
  licensedSourceMapping?: string;
}

export interface LessonProgress {
  lessonId: string;
  inkPassed: boolean;
  earStreak: number;
  earBest: number;
  creativeDone: boolean;
  /** ms since epoch of the last time this lesson was practised. */
  lastPractisedAt: number | null;
  /** How many times it has come back around for review. */
  reviewCount: number;
  /** ms since epoch when it should next be reviewed, or null if never mastered. */
  dueAt: number | null;
}

export function emptyProgress(lessonId: string): LessonProgress {
  return {
    lessonId,
    inkPassed: false,
    earStreak: 0,
    earBest: 0,
    creativeDone: false,
    lastPractisedAt: null,
    reviewCount: 0,
    dueAt: null,
  };
}

/** Has this lesson met its own mastery rule? */
export function isMastered(lesson: Lesson, progress: LessonProgress): boolean {
  const rule = lesson.masteryRule;
  if (rule.requireInk && !progress.inkPassed) return false;
  if (rule.requireCreative && !progress.creativeDone) return false;
  return progress.earBest >= rule.earStreak;
}
